// BrandingSection — sits inside the DomainOverviewTab.
//
// Extends the existing per-domain edit surface with three fields
// (`custom_color`, `custom_logo_url`, `custom_icon_url`), a live
// preview of the four surfaces that will re-theme (Sidebar lockup,
// SiteScopeBadge, Dashboard tab underline, Training hero top rule),
// and a save-time contrast guard.
//
// PATCH target: /api/admin/domains/{id}  (existing DomainUpdate model
// already accepts these three fields — no new endpoint needed).
import React, { useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, Upload, X } from 'lucide-react';
import {
  bestTextOn, rgba, darkenForText, hexToRgb, evaluateAccentPair,
} from '../../../lib/tenantTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const YABBIT_DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png';

// Resolve a stored URL to something the browser can render — the
// backend returns paths like `/api/files/branding/…` which need the
// preview URL prefixed so they load correctly in the admin panel.
const resolveAssetUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_ORIGIN}${u}`;
};

/** Save-time contrast guard — returns `null` when the pair passes both
 *  required checks, else a specific reason string. Enforces spec item
 *  14: 4.5:1 as text-on-white AND 3:1 as accent-fill with the tenant's
 *  chosen accent_text on top (defaults to white if blank). Empty accent
 *  is always allowed (means "use platform defaults"). */
const contrastReason = (accentHex, accentTextHex) => {
  const res = evaluateAccentPair(accentHex, accentTextHex);
  return res.ok ? null : res.msg;
};

const AssetPicker = ({ label, hint, kind, value, onChange, token, testId }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large — 2MB max.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/admin/uploads/branding?kind=${encodeURIComponent(kind)}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      onChange(r.data?.url || '');
      toast.success('Uploaded — save to apply.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const clear = () => onChange('');
  const preview = resolveAssetUrl(value);
  return (
    <div>
      <span className="text-white/70 text-xs font-medium">{label}</span>
      <div className="mt-1 flex items-start gap-2">
        <div className="w-14 h-14 shrink-0 rounded bg-white/95 border border-white/10 flex items-center justify-center overflow-hidden" data-testid={`${testId}-preview`}>
          {preview ? (
            <img src={preview} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
          ) : (
            <span className="text-[9px] text-slate-400 uppercase tracking-widest">Empty</span>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…  or upload below"
            className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-white text-xs focus:outline-none focus:border-teal-400"
            data-testid={`${testId}-url`}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 text-xs bg-teal-500/80 hover:bg-teal-400 disabled:opacity-40 text-white rounded px-2.5 py-1 font-semibold"
              data-testid={`${testId}-upload-btn`}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload
            </button>
            {value && (
              <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-rose-300" data-testid={`${testId}-clear-btn`}>
                <X className="w-3 h-3" /> Clear
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              onChange={handlePick}
              className="hidden"
            />
          </div>
          {hint && <p className="text-[10px] text-white/40">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

export const BrandingSection = ({ domain, domainId, token, onRefresh }) => {
  const [color, setColor] = useState(domain?.custom_color || '');
  const [accentTextColor, setAccentTextColor] = useState(domain?.custom_accent_text_color || '');
  const [logoUrl, setLogoUrl] = useState(domain?.custom_logo_url || '');
  const [iconUrl, setIconUrl] = useState(domain?.custom_icon_url || '');
  const [busy, setBusy] = useState(false);

  const initialColor = domain?.custom_color || '';
  const initialAccentText = domain?.custom_accent_text_color || '';
  const initialLogo = domain?.custom_logo_url || '';
  const initialIcon = domain?.custom_icon_url || '';
  const dirty = color !== initialColor
    || accentTextColor !== initialAccentText
    || logoUrl !== initialLogo
    || iconUrl !== initialIcon;

  const err = useMemo(
    () => contrastReason(color.trim(), accentTextColor.trim()),
    [color, accentTextColor],
  );

  const handleSave = async () => {
    const reason = contrastReason(color.trim(), accentTextColor.trim());
    if (reason) {
      toast.error(reason);
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/domains/${domainId}`,
        {
          custom_color: color.trim(),
          custom_accent_text_color: accentTextColor.trim(),
          custom_logo_url: logoUrl.trim(),
          custom_icon_url: iconUrl.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Branding saved — users on this tenant will see the new theme on next page load.');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save branding');
    } finally { setBusy(false); }
  };

  const previewAccent = color.trim() || '#0B9DB6';   // Yabbit teal fallback
  const previewBadgeBg = color.trim() ? rgba(color.trim(), 0.12) : 'rgb(255 251 235)';
  const previewBadgeBorder = color.trim() ? rgba(color.trim(), 0.35) : 'rgb(253 230 138)';
  const previewBadgeText = color.trim() ? darkenForText(color.trim()) : 'rgb(146 64 14)';
  // Prefer the tenant's locked accent-text if set; else auto-pick.
  const previewFillText = accentTextColor.trim() && hexToRgb(accentTextColor.trim())
    ? accentTextColor.trim()
    : (color.trim() ? bestTextOn(color.trim()) : '#ffffff');

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="branding-editor">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-white font-semibold">Branding</h3>
          <p className="text-xs text-white/50 mt-0.5 max-w-md">
            Tenant-specific accent colour + logo assets. Leave the accent blank to
            keep the default Yabbit teal — Yabbit&apos;s brand chrome will render for
            every user on this domain. The &quot;Powered by Yabbit VoiceFabric&quot; credit
            below the wordmark is always shown, non-configurable.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Editors */}
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-white/70 text-xs font-medium">Accent colour (hex)</span>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={color || '#0B9DB6'}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10 shrink-0"
                data-testid="branding-color-picker"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#CDA177"
                maxLength={9}
                className="flex-1 bg-black/30 border border-white/10 rounded px-3 py-2 text-white font-mono text-xs focus:outline-none focus:border-teal-400"
                data-testid="branding-color-hex"
              />
            </div>
            {err && (
              <p className="mt-1.5 text-xs text-rose-300 flex items-start gap-1.5" data-testid="branding-color-error">
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {err}
              </p>
            )}
          </label>
          <label className="block">
            <span className="text-white/70 text-xs font-medium">Text on the accent fill</span>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={accentTextColor || '#ffffff'}
                onChange={(e) => setAccentTextColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10 shrink-0"
                data-testid="branding-accent-text-picker"
              />
              <input
                type="text"
                value={accentTextColor}
                onChange={(e) => setAccentTextColor(e.target.value)}
                placeholder="Blank = auto (black on light, white on dark)"
                maxLength={9}
                className="flex-1 bg-black/30 border border-white/10 rounded px-3 py-2 text-white font-mono text-xs focus:outline-none focus:border-teal-400"
                data-testid="branding-accent-text-hex"
              />
            </div>
            <p className="mt-1 text-[10px] text-white/40">
              Locks the label colour used on buttons, active nav, and the banner. Blank auto-picks the higher-contrast of white/black — set <code className="bg-white/10 px-1 rounded">#000000</code> for warm tan/gold accents to hit WCAG AA.
            </p>
          </label>
          <AssetPicker
            label="Wordmark logo"
            hint="Wide horizontal lockup for the sidebar. Falls back to the Yabbit landscape when unset."
            kind="logo"
            value={logoUrl}
            onChange={setLogoUrl}
            token={token}
            testId="branding-logo"
          />
          <AssetPicker
            label="Icon"
            hint="Square icon shown left of the wordmark. Optional — leave blank to hide."
            kind="icon"
            value={iconUrl}
            onChange={setIconUrl}
            token={token}
            testId="branding-icon"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || busy || !!err}
            className="w-full mt-2 inline-flex items-center justify-center gap-1.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg px-4 py-2"
            data-testid="branding-save-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save branding
          </button>
        </div>

        {/* Live preview — the 4 surfaces from the spec */}
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-4 space-y-4" data-testid="branding-preview">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Sidebar lockup</p>
            <div className="rounded bg-white p-3 flex items-center gap-3" data-testid="branding-preview-lockup">
              {iconUrl ? (
                <img src={resolveAssetUrl(iconUrl)} alt="" className="h-8 w-8 object-contain" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
              ) : null}
              <div className="min-w-0">
                <img src={resolveAssetUrl(logoUrl) || YABBIT_DEFAULT_LOGO} alt="" className="h-8 w-auto object-contain max-w-[140px]" onError={(e) => { e.target.src = YABBIT_DEFAULT_LOGO; }} />
                <p className="text-[9px] uppercase tracking-widest text-slate-400 font-medium leading-none mt-0.5">
                  Powered by Yabbit VoiceFabric
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">SiteScopeBadge</p>
            <div className="rounded bg-white p-3" data-testid="branding-preview-badge">
              <span
                className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
                style={{ backgroundColor: previewBadgeBg, borderColor: previewBadgeBorder, color: previewBadgeText }}
              >
                <span className="text-[10px] uppercase tracking-wider">Scope</span>
                <span>Chermside</span>
              </span>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Active nav item + tab underline</p>
            <div className="rounded bg-white p-3 space-y-2" data-testid="branding-preview-nav">
              <div
                className="inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium"
                style={{ backgroundColor: previewAccent, color: previewFillText }}
              >
                Active nav item
              </div>
              <div className="pt-2">
                <span
                  className="inline-block px-4 py-2 text-sm font-medium text-slate-900 bg-slate-50 rounded-lg"
                  style={{ borderBottom: `2px solid ${previewAccent}` }}
                >
                  Active tab
                </span>
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Training hero top rule</p>
            <div
              className="rounded p-3"
              style={{ backgroundColor: '#faf7ee', borderTop: `4px solid ${previewAccent}` }}
              data-testid="branding-preview-hero"
            >
              <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: previewAccent }}>
                VoiceFabric &gt; Total Fusion
              </p>
              <p className="text-sm font-bold" style={{ color: '#1a3d2e' }}>Total Fusion VoiceFabric Training</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingSection;
